/**
 * A critical notice fills the screen (§37.82–83, step 61 task 10): it cannot
 * hide behind the bell's badge. Red, its title and body, and one big "I've
 * read this" — never a timer, never Escape, never a tap beside it.
 *
 * Two ways in, ONE screen at a time (`useEmergencySlot`):
 *
 * - **The feed** (`FeedEmergency`, mounted by the header's `HeaderInbox`, so
 *   in every shell, for a signed-in person): an unread `emergency` (or
 *   CRITICAL) notification from the last 24 h. "I've read this" marks it
 *   read on the server and remembers it on this device
 *   (`myrave:platform:emergency-ack`), so a slow server never shows it twice.
 * - **The stored bundle** (`UrgentAlert` in `visitor/UrgentBanner.tsx`), with
 *   no signal too: a current URGENT announcement of the rave on screen.
 *
 * An acknowledged URGENT announcement is written to that rave's
 * `myrave:{editionId}:urgent-ack` by EITHER way, so the feed's copy of a
 * notice and the bundle's never interrupt somebody twice. While one shows,
 * the header's pop-ups are held (`useEmergencyHeld`) — they wait their turn.
 *
 * Never inside the wizard's framed preview or a public preview: there an
 * admin is LOOKING at a rave, not at the field.
 */
import { useEffect, useId, useRef, useState, useSyncExternalStore, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { focusables } from '@/ui';
import { lockScroll } from '@/ui/Sheet';
import { api, type NotificationPage, type NotificationRow } from '@/lib/api';
import { readJson, writeJson } from '@/lib/storage';
import { useCurrentRave } from '@/lib/currentRave';
import { isFramed } from '@/lib/framed';
import { inPreview } from '@/lib/preview';
import { useT } from '@/i18n';
import './EmergencyScreen.css';

/* ---- one screen at a time ------------------------------------------------- */

let owner: object | null = null;
const listeners = new Set<() => void>();
function setOwner(next: object | null): void {
  if (owner === next) return;
  owner = next;
  for (const l of listeners) l();
}
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

/** True while an emergency fills the screen — the header holds its pop-ups. */
export function useEmergencyHeld(): boolean {
  return useSyncExternalStore(subscribe, () => owner !== null);
}

/**
 * Claim the one screen for `id` (null: nothing to show). True when this
 * caller holds it; a second caller waits until the first lets go.
 */
export function useEmergencySlot(id: string | null): boolean {
  const token = useRef({}).current;
  const current = useSyncExternalStore(subscribe, () => owner);
  useEffect(() => {
    if (id && current === null) setOwner(token);
    else if (!id && current === token) setOwner(null);
  }, [id, current, token]);
  useEffect(() => () => { if (owner === token) setOwner(null); }, [token]);
  return Boolean(id) && current === token;
}

/** Never in the wizard's framed preview or a public preview. */
export function emergencyScreenAllowed(): boolean {
  return !isFramed() && !inPreview();
}

/* ---- the screen ------------------------------------------------------------ */

export function EmergencyAlert({ title, body, rave, onRead, onOpen }: {
  title: string;
  body?: string | null;
  /** The rave it is about, when it is about one. */
  rave?: string | null;
  onRead: () => void;
  /** "Open the rave" — only when there is somewhere to go. */
  onOpen?: () => void;
}) {
  const tr = useT();
  const id = useId();
  const panel = useRef<HTMLDivElement>(null);
  const ack = useRef<HTMLButtonElement>(null);
  // The page holds still, and focus comes in — back where it was afterwards.
  useEffect(() => {
    const unlock = lockScroll();
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => { unlock(); if (opener && opener.isConnected) opener.focus(); };
  }, []);
  // Each new notice starts at its button.
  useEffect(() => { ack.current?.focus(); }, [title]);

  /** Tab goes round inside it, never out behind it. */
  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Tab' || !panel.current) return;
    const items = focusables(panel.current);
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) { e.preventDefault(); return; }
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  return createPortal(
    <div className="emergency-alert-scrim">
      <div
        ref={panel}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={body ? `${id}-body` : undefined}
        className="emergency-alert"
        onKeyDown={onKeyDown}
      >
        <div className="emergency-alert-mark" aria-hidden="true">🚨</div>
        {rave && <div className="emergency-alert-rave">{rave}</div>}
        <div className="emergency-alert-label">{tr('Emergency')}</div>
        <h2 id={`${id}-title`} className="emergency-alert-title">{title}</h2>
        {body && <p id={`${id}-body`} className="emergency-alert-body">{body}</p>}
        <div className="emergency-alert-actions">
          {onOpen && (
            <button type="button" className="emergency-alert-open" onClick={onOpen}>{tr('Open the rave')}</button>
          )}
          <button ref={ack} type="button" className="emergency-alert-ack" onClick={onRead}>{tr('I’ve read this')}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ---- from the feed --------------------------------------------------------- */

const ACK_KEY = 'emergency-ack';
/** The rave's own list, shared with the bundle's alert (`UrgentAlert`). */
export const URGENT_ACK_KEY = 'urgent-ack';
const DAY_MS = 86_400_000;

export function isCritical(n: Pick<NotificationRow, 'kind' | 'priority'>): boolean {
  return n.kind === 'emergency' || n.priority === 'CRITICAL';
}

/** Remember an URGENT announcement as read on this device, for that rave. */
export function ackAnnouncement(editionId: string, announcementId: string): void {
  const list = readJson<string[]>(editionId, URGENT_ACK_KEY, []);
  if (!list.includes(announcementId)) writeJson(editionId, URGENT_ACK_KEY, [...list, announcementId].slice(-100));
}

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

/**
 * The feed's way in. `count` is the person's unread notifications across
 * every rave (the header's `['me','unread','all']`); when it is above nought
 * the unread list is asked for — again whenever it changes.
 */
export function FeedEmergency({ count, open }: { count: number; open: (url: string) => void }) {
  const qc = useQueryClient();
  const rave = useCurrentRave();
  const [allowed] = useState(emergencyScreenAllowed);
  const [, setVersion] = useState(0);
  const list = useQuery({
    queryKey: ['me', 'notifications', 'unread-critical', count],
    queryFn: () => api.get<NotificationPage>('/me/notifications?unread=true&limit=20'),
    enabled: allowed && count > 0,
    staleTime: 15_000,
    retry: false,
  });
  const acked = readJson<string[]>(null, ACK_KEY, []);
  const now = Date.now();
  const current = allowed && count > 0
    ? (list.data?.notifications ?? [])
      .filter((n) => {
        if (!isCritical(n) || n.readAt || n.cancelledAt || n.supersededById || acked.includes(n.id)) return false;
        if (now - Date.parse(n.createdAt) > DAY_MS) return false;
        const ed = n.editionId ?? str(n.data?.editionId);
        const ann = str(n.data?.announcementId);
        return !(ed && ann && readJson<string[]>(ed, URGENT_ACK_KEY, []).includes(ann));
      })
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0] ?? null
    : null;
  const mine = useEmergencySlot(current?.id ?? null);
  if (!current || !mine) return null;

  const edition = current.editionId ?? str(current.data?.editionId);
  const raveName = str(current.data?.raveName) ?? (rave && rave.editionId === edition ? rave.name : null);
  const url = str(current.data?.url);
  const read = () => {
    writeJson(null, ACK_KEY, [current.id, ...acked].slice(0, 100));
    const ann = str(current.data?.announcementId);
    if (edition && ann) ackAnnouncement(edition, ann);
    setVersion((v) => v + 1);
    void api.post('/me/notifications/read', { ids: [current.id] })
      .then(() => {
        void qc.invalidateQueries({ queryKey: ['me', 'unread'] });
        void qc.invalidateQueries({ queryKey: ['me', 'notifications'] });
      })
      .catch(() => undefined);
  };
  return (
    <EmergencyAlert
      title={current.title}
      body={current.body}
      rave={raveName}
      onRead={read}
      onOpen={url && /^https?:\/\//.test(url) ? () => { read(); open(url); } : undefined}
    />
  );
}
